/**
 * Face position validator.
 *
 * @copyright Realeyes OU. All rights reserved.
 */

import BaseValidator from './BaseValidator';
import FaceDetector from './FaceDetector';
import frontalface from './utils/frontalface';
import ValidationResult from './ValidationResult';

const STATE_IDLE = 'idle';
const STATE_ACTIVE = 'active';

/**
 * Checks if the face is close enough to the webcam by checking faceWidth / imageWidth ratio.
 *
 * @param {number} faceMinSizeRatio Minimal ratio of face size to image width
 * @param {number} imageWidth Image width
 * @param {number} faceWidth Face width
 * @return {boolean}
 */
function checkFaceSizeRatio(faceMinSizeRatio, imageWidth, faceWidth) {
    return faceWidth / imageWidth > faceMinSizeRatio;
}

/**
 * Checks if the face rectangle center point is in the central part of the image.
 *
 * @param {number} faceCenterAreaRatio Ratio of center rectangle side to image side
 * @param {number} imageWidth Image width
 * @param {number} imageHeight Image height
 * @param {{ x: number, y: number, width: number, height: number }} faceRect Detected rectangle
 * @return {boolean}
 */
function checkFacePosition(faceCenterAreaRatio, imageWidth, imageHeight, faceRect) {
    const centerPoint = {
        x: (faceRect.x + (faceRect.width / 2)) | 0,
        y: (faceRect.y + (faceRect.height / 2)) | 0,
    };
    const centerArea = {
        x: ((imageWidth - (faceCenterAreaRatio * imageWidth)) / 2) | 0,
        y: ((imageHeight - (faceCenterAreaRatio * imageHeight)) / 2) | 0,
        width: (faceCenterAreaRatio * imageWidth) | 0,
        height: (faceCenterAreaRatio * imageHeight) | 0,
    };

    return centerPoint.x > centerArea.x
           && centerPoint.x < centerArea.x + centerArea.width
           && centerPoint.y > centerArea.y
           && centerPoint.y < centerArea.y + centerArea.height;
}

class FacePositionValidator extends BaseValidator {
    /**
     * Creates a FacePositionValidator instance.
     *
     * @param {MediaStream} stream Stream
     * @param {number} interval Detection interval
     * @param {number} passedCount Number of consecutive checks to be valid
     * @param {number} faceMinSizeRatio Minimal ratio of face size to image width
     * @param {number} faceCenterAreaRatio Ratio of center rectangle side to image side
     * @param {number} brightnessLow Face brightness low rate limit
     * @param {number} brightnessHigh Face brightness high rate limit
     * @param {number} maxWorkSize Biggest dimension to which the source image will be resized
     * @param {number} startScale Initial scale
     * @param {number} scaleFactor Scale factor for each step
     */
    constructor(stream, {
        interval = 250,
        passedCount = 3,
        faceMinSizeRatio = 0.1,
        faceCenterAreaRatio = 0.9,
        brightnessLow = 0.1,
        brightnessHigh = 2,
        maxWorkSize = 200,
        startScale = 2,
        scaleFactor = 1.15,
    } = {}) {
        super();
        this.stream = stream;
        this.interval = interval;
        this.passedCount = passedCount;
        this.faceMinSizeRatio = faceMinSizeRatio;
        this.faceCenterAreaRatio = faceCenterAreaRatio;
        this.brightnessLow = brightnessLow;
        this.brightnessHigh = brightnessHigh;
        this.maxWorkSize = maxWorkSize;
        this.startScale = startScale;
        this.scaleFactor = scaleFactor;
        this.state = STATE_IDLE;
        this.results = [];
    }

    /**
     * Starts validation.
     *
     * @override
     * @return {Promise}
     */
    start() {
        this.state = STATE_ACTIVE;

        return this.stream.getVideoTrack()
            .then((videoTrack) => {
                if (videoTrack === undefined) throw new Error('Cannot find video track in the stream');

                const faceDetector = new FaceDetector(
                    frontalface,
                    videoTrack.getWidth(),
                    videoTrack.getHeight(),
                    this.maxWorkSize,
                    this.startScale,
                    this.scaleFactor,
                );

                const tick = () => {
                    videoTrack.getImage()
                        .then(image => [image.bytes, faceDetector.detect(image.bytes)])
                        .then(([image, rect]) => {
                            if (rect === null) {
                                this.results.push(ValidationResult.NO_FACE);
                            } else if (!checkFaceSizeRatio(this.faceMinSizeRatio, videoTrack.getWidth(), rect.width)) {
                                this.results.push(ValidationResult.FACE_TOO_FAR);
                            } else if (!checkFacePosition(
                                this.faceCenterAreaRatio,
                                videoTrack.getWidth(),
                                videoTrack.getHeight(),
                                rect,
                            )) {
                                this.results.push(ValidationResult.FACE_OFF_CENTER);
                            } else {
                                this.results.push(ValidationResult.OK);
                            }

                            this.processResults();
                        });
                };

                this.intervalId = setInterval(tick, this.interval);
                tick();
            });
    }

    /**
     * Stops validation.
     *
     * @override
     * @return {Promise}
     */
    stop() {
        this.state = STATE_IDLE;
        clearInterval(this.intervalId);

        return Promise.resolve();
    }

    /**
     * Processes the results buffer and emits the result event.
     *
     * @return {undefined}
     */
    processResults() {
        if (this.state !== STATE_ACTIVE) return;

        this.results = this.results.slice(-this.passedCount);

        if (this.results.every(r => r === ValidationResult.OK)) {
            this.emit('result', ValidationResult.OK);
            return;
        }

        // emit last result that is not OK
        for (let i = this.results.length - 1; i >= 0; i--) {
            // istanbul ignore else
            // TODO: to test else branch we need a VideoTrack mock that is
            // TODO: capable of producing both valid and invalid images
            if (this.results[i] !== ValidationResult.OK) {
                this.emit('result', this.results[i]);
                return;
            }
        }
    }
}

export default FacePositionValidator;
